import { useState } from 'react';

/**
 * Una sección que se abre cuando hace falta.
 *
 * Nace del formulario de trabajo, que en un teléfono medía dos pantallas y
 * media **vacío**. El problema no era la cantidad de campos —todos hacen
 * falta— sino que estuvieran todos desplegados a la vez: para llegar de
 * «cliente» a «guardar» había que pasar por la movilidad, el reparto y los
 * estados administrativos, que en la mayoría de los trabajos quedan como
 * vienen.
 *
 * La regla que sigue es divulgación progresiva, no esconder: **cerrada, la
 * sección muestra su contenido resumido**. Se ve que la movilidad son 50 km y
 * $42.550 sin abrir nada; lo que se pliega son los controles, no la
 * información. Una sección que oculta su propio estado no ahorra scroll, hace
 * dudar.
 *
 * Y se abre sola cuando algo está mal. Plegar un problema sería peor que el
 * scroll que se quiso evitar.
 */
export function Plegable({
  titulo,
  resumen,
  alerta = false,
  defectoAbierto = false,
  testId,
  children,
}: {
  titulo: string;
  /** Qué dice la sección cuando está cerrada. */
  resumen: React.ReactNode;
  /** Algo requiere atención: se abre y no se deja cerrar por descuido. */
  alerta?: boolean;
  defectoAbierto?: boolean;
  testId?: string | undefined;
  children: React.ReactNode;
}) {
  const [abierto, setAbierto] = useState(defectoAbierto);
  const visible = abierto || alerta;

  return (
    <section
      // El aviso se marca con el borde de peligro, no con un color suelto de
      // Tailwind: `border-red-300` no estaba en la paleta y ademÃ¡s pisaba el
      // borde de la tarjeta sin respetar su grosor.
      className="flex flex-col superficie"
      style={alerta ? { borderColor: 'var(--peligro)', borderWidth: 2 } : undefined}
      data-testid={testId}
    >
      <button
        type="button"
        onClick={() => setAbierto((v) => !v)}
        aria-expanded={visible}
        className="flex min-h-14 items-center gap-3 px-4 py-3 text-left"
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        <span className="min-w-0 flex-1">
          <span className="block font-medium">{titulo}</span>
          <span
            className="block truncate text-xs text-[var(--tinta-suave)]"
            data-testid={testId ? `${testId}-resumen` : undefined}
          >
            {resumen}
          </span>
        </span>
        <span aria-hidden className="shrink-0 opacity-60">
          {visible ? '▴' : '▾'}
        </span>
      </button>

      {visible && (
        <div className="flex flex-col gap-3 border-t border-[var(--linea)] px-4 py-3">
          {children}
        </div>
      )}
    </section>
  );
}
